<template>
    <!--
        The navbar strip: `h-(--ui-header-height)` with a bottom hairline, the page TITLE at the
        left and the controls at the right. The title is the breadcrumb trail of §9.1 — on a flat
        screen its one crumb is the current section's menu label, the same word the sidebar shows,
        and on an instance page it grows into *Client › Instance › Tab*.

        It paints `--ui-bg-chrome` — the same surface the sidebar takes, so THE FRAME IS ONE
        SURFACE: white over the neutral-100 canvas in light, and in dark the panel colour, the
        same mix the cards take, over the darker neutral-900 canvas. The hairline below it is
        the seam, not the separation.

        The user menu is NOT here — it lives at the bottom of the sidebar, where an admin console
        puts it. What stays is what belongs to the whole window rather than to the person: the
        Cmd+K palette's trigger and the light/dark toggle (vue-spec `theme.md`).
    -->
    <header class="h-(--ui-header-height) shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-default bg-(--ui-bg-chrome)">
        <div class="flex items-center gap-2 min-w-0">
            <!-- Opens the drawer. It does not have to close it: once open, the drawer covers
                 this corner, and the X inside it and the backdrop are the ways out. -->
            <UButton
                v-if="isMobile"
                color="neutral"
                variant="ghost"
                icon="i-lucide-menu"
                :aria-label="$t('layout.open_menu')"
                @click="openMenu"
            />
            <UBreadcrumb :items="crumbs" :ui="{ root: 'min-w-0' }" />
        </div>

        <div class="flex items-center shrink-0 gap-1">
            <UTooltip :text="$t('general.search')" :kbds="['meta', 'K']">
                <UButton
                    color="neutral"
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-search"
                    :aria-label="$t('general.search')"
                    @click="openSearch"
                />
            </UTooltip>
            <ColorModeToggle />
        </div>
    </header>
</template>

<script lang="ts" setup>
    import type { BreadcrumbItem } from '@nuxt/ui'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute } from 'vue-router'

    import ColorModeToggle from '@/layouts/components/ColorModeToggle.vue'
    import { useLayout, useSearch } from '@/layouts/layout'
    import { useFleetStore } from '@/stores/fleet'

    /**
     * The breadcrumb of §9.1 — the *Client › Instance › Tab* trail.
     *
     * Three sources, in strict priority order:
     *
     *  1. `meta.breadcrumb` on the route, rendered verbatim. Keeping the reading of that field
     *     here — rather than inventing a breadcrumb store — is what makes the deep-linkable
     *     instance tabs a routing concern and nothing else.
     *  2. The route's own parameters, resolved to names through `stores/fleet.ts`. The cache is
     *     the reason this component issues no requests of its own: a breadcrumb that fetched per
     *     navigation would be one round trip in front of every screen, and it is *this* trail —
     *     not the sidebar — that tells an operator which customer's production edge they are
     *     about to change.
     *  3. The route name, translated through `nav.*`, which is what every flat screen shows.
     *
     * A name the cache has not learned yet degrades to the level being skipped rather than to a
     * placeholder: "Acme › " with an empty crumb is worse than no crumb at all, and the instance
     * page seeds the cache from its own reads (`fleetStore.remember`) precisely so the cold
     * deep-link case resolves on first paint.
     */
    const route = useRoute()
    const { t, te } = useI18n()

    const { isMobile, openMenu } = useLayout()
    const { openSearch }         = useSearch()

    const fleetStore = useFleetStore()

    const label = (name: string): string => {
        const key = `nav.${name}`
        return te(key) ? t(key) : name
    }

    const param = (name: string): string => {
        const value = route.params[name]
        return typeof value === 'string' ? value : ''
    }

    /** The Client › Instance › Tab trail, built for any route carrying an `instanceId`. */
    const instanceCrumbs = (instanceId: string): BreadcrumbItem[] => {
        const items: BreadcrumbItem[] = []
        const instance = fleetStore.instanceById(instanceId)

        if (instance?.client_name) {
            items.push({
                label: instance.client_name,
                icon:  'i-lucide-building-2',
                to:    { name: 'client', params: { clientId: instance.client_id } },
            })
        }

        items.push({
            label: instance?.name || t('nav.instances'),
            icon:  'i-lucide-server',
            to:    { name: 'instance-overview', params: { instanceId } },
        })

        // The tab is the leaf and is deliberately not a link: it is where we already are.
        const tab = typeof route.meta.tab === 'string' ? route.meta.tab : ''
        if (tab) items.push({ label: t(`instance.tabs.${tab}`) })

        return items
    }

    const crumbs = computed<BreadcrumbItem[]>(() => {
        const declared = route.meta.breadcrumb as BreadcrumbItem[] | undefined
        if (declared?.length) return declared

        const instanceId = param('instanceId')
        if (instanceId) return instanceCrumbs(instanceId)

        const clientId = param('clientId')
        if (clientId) {
            return [
                { label: t('nav.clients'), icon: 'i-lucide-building-2', to: { name: 'clients' } },
                { label: fleetStore.clientNameOf(clientId) || t('client.client') },
            ]
        }

        // /users/:userId — there is no fleet-like name cache for users (§9.2 names exactly one
        // such cache, and a user is not fleet data any more than it is an `AccessScope` subject —
        // see `Logic.ListUsers`'s own comment), so the leaf is a generic label rather than a name
        // fetched a second time just for the trail.
        const userId = param('userId')
        if (userId) {
            return [
                { label: t('nav.users'), icon: 'i-lucide-users', to: { name: 'users' } },
                { label: t('users.user') },
            ]
        }

        const name = typeof route.name === 'string' ? route.name : ''
        return name ? [{ label: label(name) }] : []
    })
</script>
